import { useRef } from 'react';
import { useSnackbar } from 'notistack';
import { Button } from '@components/button';
import { FileUpload } from '@assets/svgs';
import { Tooltip } from '@components/tooltip';

export interface UploadFileProps {
  /** Accepted file types (e.g., ['.xlsx', '.xls'] */
  fileTypes: string[];
  /** Maximum file size in MB */
  maxSizeMB: number;
  /** Handler called when file passes all validation checks */
  onUpload: (file: File) => void;
  /** Button text - @defaults to "Upload File" */
  buttonText?: string;
  /** Indicates if the upload is in progress */
  isLoading?: boolean;
}

export const UploadFile: React.FC<UploadFileProps> = ({
  fileTypes,
  maxSizeMB,
  onUpload,
  isLoading,
  buttonText = 'Upload File',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const validateFileType = (file: File): boolean => {
    // Check both file extension and MIME type
    const fileName = file.name.toLowerCase();
    const fileMimeType = file.type.toLowerCase();

    return fileTypes.some((type) => {
      const normalizedType = type.toLowerCase();
      // Check if it's a file extension (starts with .)
      if (normalizedType.startsWith('.')) {
        return fileName.endsWith(normalizedType);
      }
      // Check if it's a MIME type
      return fileMimeType === normalizedType;
    });
  };

  const validateFileSize = (file: File): boolean => {
    const fileSizeMB = file.size / (1024 * 1024);
    return fileSizeMB <= maxSizeMB;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!validateFileType(file)) {
      enqueueSnackbar('Wrong File Type', { variant: 'error' });
      return;
    }

    // Validate file size
    if (!validateFileSize(file)) {
      enqueueSnackbar(`File exceeds ${maxSizeMB}MB`, { variant: 'error' });
      return;
    }

    onUpload(file);

    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept={fileTypes.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <Tooltip title='Only .xlsx files allowed, 10MB maximum. Please ensure they are in the Moratorium upload format.'>
        <Button isLoading={isLoading} variant={'text'} onClick={handleButtonClick} startIcon={<FileUpload />}>
          {buttonText}
        </Button>
      </Tooltip>
    </>
  );
};
